import { useState, useMemo, useEffect } from "react";
import { Plus, Banknote, Wallet, ArrowDownLeft, ArrowUpRight, ArrowLeftRight, X, Pencil, TrendingUp, RefreshCw, Upload, CalendarRange, CheckCheck } from "lucide-react";
import { Page, Panel, Table, DeleteButton, StatusPill, SearchInput } from "../lib/ui";
import { money, can, humanDate, api, moneyCur, goldTL, today, rowLink, goTo } from "../lib/api";
import { AccountSummaryModal } from "./AccountSummary";

function ExchangeRatesWidget() {
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [day, setDay] = useState(today());
  const isToday = day === today();
  const load = (force = false) => {
    setLoading(true); setErr("");
    if (!isToday) setRates(null);
    const url = isToday ? `/exchange-rates${force ? "?force=true" : ""}` : `/exchange-rates/history?date=${day}`;
    api.get(url)
      .then((r) => setRates(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Kur alınamadı"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(false); }, [day]); // eslint-disable-line react-hooks/exhaustive-deps
  if (loading && !rates && isToday) return <div className="rates-widget rates-loading">Kurlar yükleniyor…</div>;
  const r = rates || {};
  const usd = r.rates?.USD || {};
  const eur = r.rates?.EUR || {};
  const gold = r.gold || {};
  return (
    <div className="rates-widget" data-testid="rates-widget">
      <div className="rates-header">
        <TrendingUp size={14} />
        <strong>{isToday ? "Güncel Kurlar" : "Geçmiş Kurlar"}</strong>
        <small data-testid="rates-source">{loading ? "Yükleniyor…" : err ? <span className="negative" data-testid="rates-error">{err}</span> : <>{r.date || "—"} · {r.source || "TCMB"}{r.stale ? " · önbellek" : ""}{r.shifted ? " · tatil/hafta sonu, önceki iş günü" : ""}</>}</small>
        <input type="date" className="rates-date" data-testid="rates-date-input" value={day} max={today()}
               onChange={(e) => setDay(e.target.value || today())} />
        {!isToday && <button type="button" className="text-button" data-testid="rates-today" onClick={() => setDay(today())}>Bugün</button>}
        <button type="button" className="icon-button" title="Yenile" onClick={() => load(true)}
                data-testid="rates-refresh">
          <RefreshCw size={13} />
        </button>
      </div>
      <div className="rates-grid">
        <RateCard code="USD" sym="$" buy={usd.forex_buying} sell={usd.forex_selling} />
        <RateCard code="EUR" sym="€" buy={eur.forex_buying} sell={eur.forex_selling} />
        {gold.buying && <RateCard code="Altın (gr)" sym="Au" buy={gold.buying} sell={gold.selling} />}
      </div>
    </div>
  );
}
function RateCard({ code, sym, buy, sell }) {
  const fmt = (n) => n ? new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(n) : "—";
  return (
    <div className="rate-card">
      <div className="rate-code"><strong>{code}</strong><span>{sym}</span></div>
      <div className="rate-pair">
        <div><small>Alış</small><strong className="positive">{fmt(buy)}</strong></div>
        <div><small>Satış</small><strong className="negative">{fmt(sell)}</strong></div>
      </div>
    </div>
  );
}

export default function Banks({ accounts, transactions, setModal, onDeleteAccount, onDeleteTx, user, customers = [] }) {
  const [gold, setGold] = useState(null);
  useEffect(() => { api.get("/exchange-rates").then((r) => setGold(r.data?.gold || null)).catch(() => {}); }, []);
  const [selected, setSelectedRaw] = useState(() => sessionStorage.getItem("banks_selected") || null);
  const setSelected = (id) => { setSelectedRaw(id); if (id) sessionStorage.setItem("banks_selected", id); };
  const [showSummary, setShowSummary] = useState(false);
  const [checked, setChecked] = useState(() => new Set(JSON.parse(sessionStorage.getItem("banks_checked") || "[]")));
  useEffect(() => { sessionStorage.setItem("banks_checked", JSON.stringify([...checked])); }, [checked]);
  const toggleCheck = (id) => setChecked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const [q, setQ] = useState("");
  const [dir, setDir] = useState("all");
  const [cat, setCat] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const active = (selected && accounts.some((a) => a.id === selected) ? selected : null) || accounts[0]?.id;
  const activeAcc = accounts.find((a) => a.id === active);
  const isOff = !!activeAcc?.off_balance;
  const [month, setMonth] = useState(today().slice(0, 7));

  const custName = useMemo(() => {
    const m = {};
    customers.forEach((c) => { m[c.id] = c.name; });
    return m;
  }, [customers]);

  const txForAccount = useMemo(() => transactions.filter((t) => !active || t.account_id === active), [transactions, active]);

  const categories = useMemo(() => [...new Set(txForAccount.map((t) => t.category).filter(Boolean))].sort(), [txForAccount]);

  const filtered = useMemo(() => txForAccount.filter((t) => {
    if (isOff && month && !(t.date || "").startsWith(month)) return false;
    if (dir !== "all" && t.direction !== dir) return false;
    if (cat && t.category !== cat) return false;
    if (dateFrom && (t.date || "") < dateFrom) return false;
    if (dateTo && (t.date || "") > dateTo) return false;
    if (q) {
      const n = q.toLocaleLowerCase("tr");
      const hay = [
        t.description, t.category, custName[t.customer_id] || "", custName[t.proxy_customer_id] || "",
        t.date, String(t.amount ?? ""), money(t.amount),
      ].join(" ").toLocaleLowerCase("tr");
      if (!hay.includes(n)) return false;
    }
    return true;
  }), [txForAccount, q, dir, cat, dateFrom, dateTo, custName, isOff, month]);

  const filterActive = q || dir !== "all" || cat || dateFrom || dateTo;
  const sums = useMemo(() => ({
    inSum: filtered.filter((t) => t.direction === "in").reduce((s, t) => s + Number(t.amount || 0), 0),
    outSum: filtered.filter((t) => t.direction === "out").reduce((s, t) => s + Number(t.amount || 0), 0),
  }), [filtered]);
  const clearFilters = () => { setQ(""); setDir("all"); setCat(""); setDateFrom(""); setDateTo(""); };

  const openDetail = (t, e) => {
    if (e.target.closest(".row-icon") || e.target.closest("button")) return;
    goTo(`/detay/hareket/${t.id}`);
  };
  return (
    <>
      <Page
        eyebrow="BANKA & KASA"
        title="Hesap Yönetimi"
        subtitle="Banka hesaplarınızı ve kasalarınızı tek bakışta izleyin."
        action={
          <div className="button-row">
            <button data-testid="open-account-summary-button" className="secondary-button" onClick={() => setShowSummary(true)}
                    disabled={!accounts.length} title="Hesapların ay sonu bakiyeleri">
              <CalendarRange size={16} /> Hesap Özeti
            </button>
            <button data-testid="open-transfer-button" className="secondary-button"
                    onClick={() => setModal({ type: "transfer" })} disabled={accounts.length < 2}
                    title={accounts.length < 2 ? "En az iki hesap gerekli" : "Hesaplar arası transfer"}>
              <ArrowLeftRight size={16} /> Transfer
            </button>
            <button data-testid="open-bank-import-button" className="secondary-button"
                    onClick={() => setModal({ type: "bank-import", account_id: active })}
                    disabled={!accounts.length || !can(user, "create_transaction")}
                    title="Banka ekstresini yükleyip satırları tek tek onaylayın">
              <Upload size={16} /> Ekstre Yükle
            </button>
            <button data-testid="add-transaction-button" className="secondary-button"
                    onClick={() => setModal({ type: "transaction", account_id: active })}
                    disabled={!accounts.length || !can(user, "create_transaction")}
                    title={!can(user, "create_transaction") ? "Bu işlem için yetkiniz yok" : ""}>
              <Plus size={16} /> Hareket
            </button>
            <button data-testid="add-account-button" className="primary-button" onClick={() => setModal({ type: "account" })}>
              <Plus size={17} /> Yeni hesap
            </button>
          </div>
        }
      />

      <ExchangeRatesWidget />

      <section className="account-grid">
        {accounts.length ? accounts.map((a) => (
          <div
            key={a.id}
            data-testid={`account-card-${a.id}`}
            role="button"
            tabIndex={0}
            className={`account-card ${active === a.id ? "active" : ""} ${a.off_balance ? "off-balance" : ""}`}
            onClick={() => setSelected(a.id)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setSelected(a.id); }}
          >
            <div className="account-icon">{a.type === "cash" ? <Wallet size={18} /> : <Banknote size={18} />}</div>
            <div className="account-info">
              <span>{a.off_balance ? "Özel Kasa" : a.type === "cash" ? "Kasa" : a.type === "pos" ? "POS Kasa" : a.bank_name || "Banka hesabı"}</span>
              <strong>{a.name}</strong>
              <small>{a.iban || `${a.currency}`}</small>
            </div>
            <div className="account-balance">
              <span>{a.off_balance ? "Harcamalar" : "Bakiye"}</span>
              <strong className={a.off_balance ? "muted-cell" : (a.balance >= 0 ? "positive" : "negative")} data-testid={`account-balance-${a.id}`}>
                {a.off_balance ? "Gizli" : moneyCur(a.balance, a.currency, a.gold_unit)}
              </strong>
              {goldTL(a, gold) !== null && (
                <small className="gold-tl" data-testid={`account-gold-tl-${a.id}`} title="Güncel alış fiyatına göre yaklaşık değer">≈ {money(goldTL(a, gold))}</small>
              )}
              {user?.role === "admin" && (
                <button type="button" className="icon-button" title="Hesabı düzenle"
                        data-testid={`edit-account-${a.id}`}
                        {...rowLink(`/detay/hesap/${a.id}`, true)}>
                  <Pencil size={13} />
                </button>
              )}
            </div>
          </div>
        )) : (
          <div className="empty-module full">
            <strong>Henüz hesap tanımlanmadı</strong>
            <span>Banka veya kasa hesabı ekleyerek başlayın.</span>
          </div>
        )}
      </section>

      <Panel
        title="Son hareketler"
        subtitle={active ? "Seçili hesap için giriş ve çıkışlar" : "Hesap ekleyerek başlayın"}
        className="module-panel"
      >
        <div className="tx-filter-bar" data-testid="tx-filter-bar">
          <SearchInput value={q} onChange={setQ} testid="tx-search"
                       placeholder="Açıklama, kategori, cari, tutar veya tarih ara" />
          <select value={dir} data-testid="tx-dir-filter" onChange={(e) => setDir(e.target.value)}>
            <option value="all">Tümü</option>
            <option value="in">Giriş</option>
            <option value="out">Çıkış</option>
          </select>
          <select value={cat} data-testid="tx-cat-filter" onChange={(e) => setCat(e.target.value)}>
            <option value="">Tüm kategoriler</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input type="date" value={dateFrom} data-testid="tx-date-from" onChange={(e) => setDateFrom(e.target.value)} title="Başlangıç tarihi" />
          <input type="date" value={dateTo} data-testid="tx-date-to" onChange={(e) => setDateTo(e.target.value)} title="Bitiş tarihi" />
          {filterActive && (
            <button className="secondary-button" data-testid="tx-clear-filters" onClick={clearFilters}>
              <X size={14} /> Temizle
            </button>
          )}
        </div>
        {isOff && (
          <div className="tx-filter-summary" data-testid="off-month-bar" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <label>Ay: <input type="month" value={month} data-testid="off-month-input" onChange={(e) => setMonth(e.target.value)} /></label>
            <span data-testid="off-month-total">{activeAcc.name} · seçilen ay harcama: <strong className="negative">{money(sums.outSum)}</strong> ({filtered.filter((t) => t.direction === "out").length} hareket)</span>
          </div>
        )}
        {filterActive && !isOff && (
          <div className="tx-filter-summary" data-testid="tx-filter-summary">
            {filtered.length} hareket · Giriş: <strong className="positive">+{money(sums.inSum)}</strong> · Çıkış: <strong className="negative">-{money(sums.outSum)}</strong>
          </div>
        )}
        {checked.size > 0 && (
          <div className="tx-filter-summary" data-testid="tx-checked-summary" style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <CheckCheck size={15} /> {checked.size} hareket kontrol edildi
            <button className="secondary-button" data-testid="tx-clear-checks" onClick={() => setChecked(new Set())}>Tikleri kaldır</button>
          </div>
        )}
        <Table
          testid="transactions-table"
          columns={[
            { label: "✓" }, { label: "Tarih" }, { label: "Açıklama" }, { label: "Kategori" }, { label: "Yön" },
            { label: "Tutar", align: "right" }, { label: "" },
          ]}
          rows={filtered}
          empty={filterActive ? "Filtreye uyan hareket yok." : "Bu hesap için hareket yok."}
          renderRow={(t) => (
            <tr key={t.id} data-testid={`transaction-row-${t.id}`} className={`clickable-row ${checked.has(t.id) ? "tx-checked" : ""}`}
                onClick={(e) => openDetail(t, e)}
                style={{ cursor: "pointer" }}>
              <td onClick={(e) => e.stopPropagation()} style={{ width: 34 }}>
                <input type="checkbox" className="tx-check" data-testid={`tx-check-${t.id}`} checked={checked.has(t.id)}
                       title="Kontrol edildi" onChange={() => toggleCheck(t.id)} />
              </td>
              <td>{humanDate(t.date)}</td>
              <td><strong>{t.description || "—"}</strong></td>
              <td>{t.category}</td>
              <td>
                <span className={`direction ${t.direction}`}>
                  {t.direction === "in" ? <ArrowDownLeft size={12} /> : <ArrowUpRight size={12} />}
                  {t.direction === "in" ? "Giriş" : "Çıkış"}
                </span>
              </td>
              <td className={`amount ${t.direction === "in" ? "positive" : "negative"}`}>
                {t.direction === "in" ? "+" : "-"}{money(t.amount)}
              </td>
              <td className="amount">
                <DeleteButton
                  testid={`delete-tx-${t.id}`}
                  disabled={!can(user, "delete_transaction")}
                  onClick={() => onDeleteTx(t)}
                />
              </td>
            </tr>
          )}
        />
      </Panel>
      {showSummary && <AccountSummaryModal onClose={() => setShowSummary(false)} />}

      {accounts.length > 0 && (
        <Panel title="Hesaplar" className="module-panel" subtitle="Tanımlı banka ve kasa hesapları">
          <Table
            testid="accounts-table"
            columns={[{ label: "Hesap" }, { label: "Tip" }, { label: "IBAN" }, { label: "Bakiye", align: "right" }, { label: "" }]}
            rows={accounts}
            empty="—"
            renderRow={(a) => (
              <tr key={a.id} className="clickable-row" style={{ cursor: "pointer" }}
                  {...rowLink(`/detay/hesap/${a.id}`)}>
                <td><a href={`/detay/hesap/${a.id}`} className="row-anchor" onClick={(e) => e.stopPropagation()}><strong>{a.name}</strong></a><small>{a.bank_name || "Nakit kasa"}</small></td>
                <td><StatusPill status={a.type === "cash" ? "Kasa" : a.type === "pos" ? "POS" : "Banka"} /></td>
                <td>{a.iban || "—"}</td>
                <td className={`amount ${a.balance >= 0 ? "positive" : "negative"}`}>{a.off_balance ? "Gizli" : money(a.balance)}</td>
                <td className="amount" onClick={(e) => e.stopPropagation()}>
                  <DeleteButton
                    testid={`delete-account-${a.id}`}
                    disabled={!can(user, "delete_bank")}
                    onClick={() => onDeleteAccount(a)}
                  />
                </td>
              </tr>
            )}
          />
        </Panel>
      )}
    </>
  );
}
